import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ManageGallery = () => {
  const [images, setImages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    image_url: "",
    title: "",
    description: "",
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.post(`${API}/gallery`, formData, { headers });
      toast.success("Image added successfully!");
      setIsDialogOpen(false);
      setFormData({ image_url: "", title: "", description: "" });
      fetchImages();
    } catch (error) {
      console.error("Error adding image:", error);
      toast.error("Failed to add image");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.delete(`${API}/gallery/${id}`, { headers });
      toast.success("Image deleted successfully!");
      fetchImages();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  return (
    <div data-testid="manage-gallery">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manage Gallery</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-image-button">
              <Plus size={20} className="mr-2" /> Add Image
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="image-dialog">
            <DialogHeader>
              <DialogTitle>Add Gallery Image</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="image_url">Image URL</Label>
                <Input
                  id="image_url"
                  name="image_url"
                  data-testid="image-url-input"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              <div>
                <Label htmlFor="title">Title (Optional)</Label>
                <Input
                  id="title"
                  name="title"
                  data-testid="image-title-input"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  name="description"
                  data-testid="image-description-input"
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
              <Button type="submit" className="w-full" data-testid="image-submit-button">
                Add Image
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {images.map((image, index) => (
          <Card key={image.id} data-testid={`gallery-card-${index}`}>
            <CardContent className="p-4">
              <div className="aspect-square bg-gradient-to-br from-[#F0EBE3] to-orange-200 rounded-lg mb-4 flex items-center justify-center">
                <div className="text-5xl">🍸</div>
              </div>
              {image.title && <h3 className="font-semibold mb-2">{image.title}</h3>}
              {image.description && <p className="text-sm text-gray-600 mb-4">{image.description}</p>}
              <Button
                size="sm"
                variant="destructive"
                className="w-full"
                onClick={() => handleDelete(image.id)}
                data-testid={`delete-image-${index}`}
              >
                <Trash2 size={16} className="mr-2" /> Delete
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">No images yet. Click "Add Image" to upload one.</p>
        </div>
      )}
    </div>
  );
};

export default ManageGallery;
